<template>
	<div class="blain-dash">
		<div class="header">
			<h1>
				<span class="logo"></span>
				<span class="text">E-commerce Mission Control</span>
			</h1>
		</div>
		<div class="main-wrapper">
			<div class="top-wrapper">
				<div class="data-squares">
					<div class="data-square-row">
						<div class="data-square all">
							<h2><i class="fas fa-shopping-cart fa-lg fa-fw"></i> All Orders</h2>
							<div class="data">
								<animated-number :number="totalOrders"></animated-number>
								<span>checkouts today</span>
							</div>
							<div class="footer">
								<div class="col1">
									<span>new</span>
									<animated-number :number="newOrders"></animated-number>
								</div>
								<div class="col2">
									<span>returning</span>
									<animated-number :number="returningOrders"></animated-number>
								</div>
								<div class="col3">
									<span>reseller</span>
									<animated-number :number="resellerOrders"></animated-number>
								</div>
							</div>
						</div>
						<div class="data-square flag">
							<h2><i class="fas fa-flag fa-lg fa-fw"></i> Flagged</h2>
							<div class="data">
								<animated-number :number="flaggedOrders"></animated-number>
								<span>total flagged</span>
							</div>
							<div class="footer">
								<div class="col1">
									<span>new</span>
									<animated-number :number="newFlaggedOrders"></animated-number>
								</div>
								<div class="col2">
									<span>taken</span>
									<animated-number :number="takenFlaggedOrders"></animated-number>
								</div>
								<div class="col3">
									<span>clear avg</span>
									<strong>{{ flagClearAvg }}</strong>
								</div>
							</div>
						</div>
					</div>
					<div class="data-square-row">
						<div class="data-square ship">
							<h2><i class="fas fa-truck fa-lg fa-fw"></i> Ship Orders</h2>
							<div class="data">
								<animated-number :number="shipOrders"></animated-number>
								<span>awaiting fulfillment</span>
							</div>
							<div class="footer">
								<div class="col1">
									<span>in house</span>
									<animated-number :number="inHouseOrders"></animated-number>
								</div>
								<div class="col2">
									<span>third party</span>
									<animated-number :number="thirdPartyOrders"></animated-number>
								</div>
								<div class="col3">
									<span>in transit</span>
									<animated-number :number="shipInTransit"></animated-number>
								</div>
							</div>
						</div>
						<div class="data-square pickup">
							<h2><i class="fas fa-car fa-lg fa-fw"></i> Pickup Orders</h2>
							<div class="data">
								<animated-number :number="pickupOrders"></animated-number>
								<span>to be picked</span>
							</div>
							<div class="footer">
								<div class="col1">
									<span>in store</span>
									<animated-number :number="storePickupOrders"></animated-number>
								</div>
								<div class="col2">
									<span>drive thru</span>
									<animated-number :number="driveThruOrders"></animated-number>
								</div>
								<div class="col3">
									<span>ready</span>
									<animated-number :number="pickupReady"></animated-number>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="chart-wrapper">
					<div class="chart-stats">
						<div class="data-item">
							<span class="label">Projected</span>
							<animated-number :number="projectedOrders" :threshold="goalOrders"></animated-number>
						</div>
						<div class="data-item">
							<span class="label">Today's Goal</span>
							<animated-number :number="goalOrders" :goal-reached="goalReached"></animated-number>
						</div>
						<div class="data-item">
							<span class="label">Yesterday</span>
							<animated-number :number="yesterdayOrders"></animated-number>
						</div>
						<div class="data-item">
							<span class="label">Last Year</span>
							<animated-number :number="lastYearOrders"></animated-number>
						</div>
					</div>
					<div class="chart-inner-wrapper">
						<canvas id="checkoutChart" width="400" height="200"></canvas>
					</div>
				</div>
			</div>
			<div class="middle-wrapper">
				<div class="data-item long">
					<span class="label">Users</span>
					<span class="user-count" v-if="activeUserCounts != null">
						<span class="user-icon"><i class="fas fa-globe"></i></span>
						<animated-number :number="activeUserCounts.web"></animated-number>
					</span>
					<span class="user-count" v-if="activeUserCounts != null">
						<span class="user-icon"><i class="fab fa-apple"></i></span>
						<animated-number :number="activeUserCounts.ios"></animated-number>
					</span>
					<span class="user-count" v-if="activeUserCounts != null">
						<span class="user-icon"><i class="fab fa-android"></i></span>
						<animated-number :number="activeUserCounts.android"></animated-number>
					</span>
				</div>
				<div class="data-item short">
					<span class="label">Active Carts</span>
					<animated-number :number="activeCarts"></animated-number>
				</div>
				<div class="data-item short">
					<span class="label">In Checkout</span>
					<animated-number :number="activeCheckouts"></animated-number>
				</div>
				<div class="data-item mid">
					<span class="label">Checkout Velocity</span>
					<strong>{{ checkoutVelocity }}</strong>
					<span>/min</span>
				</div>
				<div class="data-item long">
					<span class="label">Last Order</span>
					<span class="lastorder">
						<span class="destination">{{ lastOrderDestination }}</span>
						<span>{{ lastOrderTime }}</span>
					</span>
				</div>
			</div>
			<div class="bottom-wrapper">
				<div class="system-summary">
					<h2>Systems</h2>
					<div class="summary-wrapper">
						<div class="summary-row">
							<span>Active Web/API Servers</span>
							<strong>{{ activeWebServers }}</strong>
						</div>
						<div class="summary-row">
							<span>Avg CPU Utilization</span>
							<strong :class="cpuClass">{{ avgCpu }}%</strong>
						</div>
						<div class="summary-row">
							<span>Main Database</span>
							<status-value :status="mainDbStatus"></status-value>
						</div>
						<div class="summary-row">
							<span>Catalog Database</span>
							<status-value :status="catDbStatus"></status-value>
						</div>
						<div class="summary-row">
							<span>Redis Cache</span>
							<status-value :status="redisStatus"></status-value>
						</div>
					</div>
				</div>
				<div class="global-avail">
					<h2>Global Availability</h2>
					<ul class="avail-wrapper">
						<li v-for="g in globalAvail" :key="g.ipAddress">
							<i v-show="g.status === 'UP'" class="fas fa-fw fa-lg fa-check ok"></i>
							<i v-show="g.status === 'DOWN'" class="fas fa-fw fa-lg fa-exclamation-triangle critical"></i>
							{{ g.regionName }}
						</li>
					</ul>
				</div>
				<div class="api-issues">
					<h2>External Service Issues</h2>
					<h3>within the last hour</h3>
					<ul class="api-wrapper">
						<li class="api-row" v-for="a in apiIssues" :key="a.apiKey">
							<span>{{ a.apiName }}</span>
							<status-value :status="a.issueCount" :since="a.timeSinceMostRecent"></status-value>
						</li>
					</ul>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import AnimatedNumber from './animated-number.vue';
	import Bugsnag from '@bugsnag/js'
	import Chart from 'chart.js';
	import StatusValue from './status-value.vue';


	let refreshInterval = null;
	let chartCreated = false;
	let myChart = null;
	let latestLabel = null;
	let firstLoad = true;
	let waitingResult = false;

	// Setup sounds
	let sonicNo = new Audio('/sounds/sonic-no.wav');
	sonicNo.volume = 0.6;
	let basso = new Audio('/sounds/basso.wav');
	basso.volume = 0.6;

	// Set up channel specs
	let channelSpecs = [
		{
			channelKey: "PaidSearch",
			channelLabel: "Paid Search",
			colorScheme: [245, 197, 41]
		},
		{
			channelKey: "ShoppingAds",
			channelLabel: "Shopping Ads",
			colorScheme: [219, 132, 0]
		},
		{
			channelKey: "OrganicSearch",
			channelLabel: "Organic",
			colorScheme: [219, 50, 54]
		},
		{
			channelKey: "Reseller",
			channelLabel: "Reseller",
			colorScheme: [30, 80, 230]
		},
		{
			channelKey: "Affiliate",
			channelLabel: "Affiliate",
			colorScheme: [255, 105, 180]
		},
		{
			channelKey: "Email",
			channelLabel: "Email",
			colorScheme: [102, 62, 152]
		},
		{
			channelKey: "Sms",
			channelLabel: "SMS",
			colorScheme: [50, 220, 220]
		},
		{
			channelKey: "Social",
			channelLabel: "Social",
			colorScheme: [53, 80, 200]
		},
		{
			channelKey: "Remarketing",
			channelLabel: "Remarketing",
			colorScheme: [100, 50, 0]
		},
		{
			channelKey: "App",
			channelLabel: "App",
			colorScheme: [50, 150, 200]
		},
		{
			channelKey: "Other",
			channelLabel: "Other",
			colorScheme: [0, 80, 0]
		},
		{
			channelKey: "Untracked",
			channelLabel: "Untracked Web",
			colorScheme: [83, 159, 46]
		}
	];

	export default {
		name: 'BlainDash',
		components: {
			AnimatedNumber,
			StatusValue
		},

		data() {
			return {
				totalOrders: 0,
				cancelledOrders: 0,
				newOrders: 0,
				returningOrders: 0,
				resellerOrders: 0,
				flaggedOrders: 0,
				newFlaggedOrders: 0,
				takenFlaggedOrders: 0,
				flagClearAvg: "",
				shipOrders: 0,
				inHouseOrders: 0,
				thirdPartyOrders: 0,
				shipInTransit: 0,
				pickupOrders: 0,
				storePickupOrders: 0,
				driveThruOrders: 0,
				pickupReady: 0,
				activeUserCounts: null,
				activeCarts: 0,
				activeCheckouts: 0,
				checkoutVelocity: 0,
				lastOrderDestination: "",
				lastOrderTime: "",
				goalOrders: 0,
				projectedOrders: 0,
				yesterdayOrders: 0,
				lastYearOrders: 0,
				activeWebServers: 0,
				avgCpu: 0,
				mainDbStatus: "",
				catDbStatus: "",
				redisStatus: "",
				globalAvail: [],
				apiIssues: [],
				chartData: null,
			};
		},

		mounted() {
			console.log("Blain dash module initiated");
			this.refreshSummary();
			refreshInterval = setInterval(this.refreshSummary, 10000);
			window.addEventListener('overscan', this.resizeChart);
		},
		
		destroyed() {
			window.removeEventListener('overscan', this.resizeChart);
			console.log("Stopping Blain dash module");
			clearInterval(refreshInterval);
			firstLoad = true;
			chartCreated = false;
			myChart = null;
		},

		computed: {
			cpuClass() {
				if (this.avgCpu >= 80) return "critical";
				if (this.avgCpu >= 50) return "warning";
				return "ok";
			},
			goalReached() {
				return this.totalOrders > this.goalOrders;
			}
		},

		watch: {
			totalOrders(newVal, oldVal) {
				if (!window.audio) return;
				if (newVal > oldVal && !firstLoad) {
					let tmp = oldVal;
					let timer = setInterval(() => {
						if (tmp >= newVal || tmp >= oldVal + 10) {
							clearInterval(timer);
							return;
						}
						tmp++;
					}, 90);
				}
			},

			mainDbStatus(newVal, oldVal) {
				this.playAlert(newVal, oldVal);
			},

			catDbStatus(newVal, oldVal) {
				this.playAlert(newVal, oldVal);
			},

			redisStatus(newVal, oldVal) {
				this.playAlert(newVal, oldVal);
			},

			globalAvail(newVal, oldVal) {
				if (!window.audio) return;

				let self = this;

				newVal.forEach(n => {
					let o = oldVal.find(f => {
						return f.ipAddress === n.ipAddress
					});
					if (o) self.playAlert(n.status, o.status);
				});
			},

			apiIssues(newVal, oldVal) {
				if (!window.audio) return;
				newVal.forEach(n => {
					let o = oldVal.find(f => {
						return f.apiKey === n.apiKey
					});

					if (o && o.issueCount < n.issueCount && o.issueCount < 10 && n.issueCount >= 10) {
						sonicNo.currentTime = 0;
						sonicNo.play();
					}
				});
			}
		},
		
		methods: {
			playAlert(newVal, oldVal) {
				if (firstLoad || !window.audio) return;
				if (newVal === "DOWN" && oldVal === "UP") {
					sonicNo.currentTime = 0;
					sonicNo.play();
				}
			},

			refreshSummary() {
				if (waitingResult) return;

				let self = this;
				waitingResult = true;
				this.$http.post('/blain/refresh-summary', { strategy: 2 }).then(response => {
					let d = response.body;

					self.totalOrders = d.todayOrders.total;
					self.cancelledOrders = d.cancelledOrders.orderCount;
					self.newOrders = d.todayOrders.new;
					self.returningOrders = d.todayOrders.returning;
					self.resellerOrders = d.todayOrders.reseller;
					self.flaggedOrders = d.flaggedOrders.total;
					self.newFlaggedOrders = d.flaggedOrders.new;
					self.takenFlaggedOrders = d.flaggedOrders.taken;
					self.flagClearAvg = d.flaggedOrders.avgHours + (d.flaggedOrders.avgHours === 1 ? " hrs" : " hr");
					self.shipOrders = d.shipOrders.total;
					self.inHouseOrders = d.shipOrders.inHouse;
					self.thirdPartyOrders = d.shipOrders.thirdParty;
					self.shipInTransit = d.shipOrders.inTransit;
					self.pickupOrders = d.pickupOrders.total;
					self.storePickupOrders = d.pickupOrders.inStore;
					self.driveThruOrders = d.pickupOrders.driveThru;
					self.pickupReady = d.pickupOrders.ready;
					self.activeUserCounts = d.liveActivity.activeUserCounts;
					self.activeCarts = d.liveActivity.activeCarts;
					self.activeCheckouts = d.liveActivity.activeCheckouts;
					self.checkoutVelocity = d.liveActivity.checkoutVelocity;
					if (d.liveActivity.mostRecentOrder.totalGiftCards > 0 && !d.liveActivity.mostRecentOrder.destinationCity && !d.liveActivity.mostRecentOrder.destinationState) {
						self.lastOrderDestination = "E-gift Card";
					} else self.lastOrderDestination = d.liveActivity.mostRecentOrder.destinationCity + ", " + d.liveActivity.mostRecentOrder.destinationState;
					self.lastOrderTime = d.liveActivity.mostRecentOrder.relativeSubmitted;
					self.goalOrders = d.chartData.goalToday;
					self.projectedOrders = d.chartData.projectedToday;
					self.yesterdayOrders = d.chartData.totalYesterday;
					self.lastYearOrders = d.chartData.totalLastYear;
					self.activeWebServers = d.activeServers.length;
					self.avgCpu = Math.round(d.avgCpuUtilization);
					self.mainDbStatus = d.ecomDbHealth;
					self.catDbStatus = d.catalogDbHealth;
					self.redisStatus = d.redisCacheHealth;
					self.globalAvail = d.globalAvailability;
					self.apiIssues = d.externalApiIssues;
					self.chartData = d.ChartData;
					
					waitingResult = false;

					// Chart data updates
					if (!chartCreated) self.createChart(d.chartData);
					else self.refreshChart(d.chartData);

					self.$nextTick(function () {
						firstLoad = false;
					});
				}).catch(e => {
					Bugsnag.notify(new Error(e.url), function (event) {
						event.addMetadata('httpResponse', e);
					});
					console.error(e);
					waitingResult = false;
				});
			},

			createEmptyDataSet() {
				return Array.apply(null, Array(49)).map(Number.prototype.valueOf, 0);
			},

			getDataArray(obj) {
				if (!obj) return this.padData(this.createEmptyDataSet());
				return this.padData(obj.map(x => { return x[1]; }));
			},

			padData(obj) {
				obj.push(null);
				obj.unshift(null);
				return obj;
			},

			makeLabels(chartData) {
				let labelTmp = chartData.countByChannel[Object.keys(chartData.countByChannel)[0]];
				let labels = labelTmp.map(x => { return x[0]; });
				latestLabel = labels[labels.length - 1];

				labels.push(moment(labels[labels.length - 1]).add(1, 'h'));
				labels.unshift(moment(labels[0]).subtract(1, 'h'));
				return labels;
			},
			
			getCurrentHourPrediction(chartData) {
				let predictionData = this.getDataArray(chartData.previousYear);
				let predictionCurrentHour = this.getDataArray();
				if (chartData.current) predictionCurrentHour[predictionData.length - 2] = chartData.current[1][1];
				return predictionCurrentHour;
			},

			createChart(chartData) {
				let self = this;
				let ctx = document.getElementById("checkoutChart");

				let dataByChannel = [];
				for (let x = 0; x < channelSpecs.length; x++) {
					let channel = channelSpecs[x];
					dataByChannel.push({
						label: channel.channelLabel,
						backgroundColor: `rgba(${channel.colorScheme.join(",")},0.5`,
						borderColor: `rgba(${channel.colorScheme.join(",")},1`,
						hoverBackgroundColor: `rgba(${channel.colorScheme.join(",")},0.5`,
						hoverBorderColor: `rgba(${channel.colorScheme.join(",")},1`,
						data: self.getDataArray(chartData.countByChannel[channel.channelKey]),
						stack: "bychannel"
					});
				}

				dataByChannel.push({
					label: "Projection",
					backgroundColor: "rgba(200,200,200,0.5)",
					borderColor: "rgba(200,200,200,1)",
					data: self.getCurrentHourPrediction(chartData),
					stack: "bychannel"
				});

				dataByChannel.push({
					label: "Previous Year",
					backgroundColor: "rgba(52,73,94,0.0)",
					borderColor: "rgba(52,73,94,0.5)",
					pointBorderColor: "rgba(52,73,94,0.0)",
					pointBackgroundColor: "rgba(52,73,94,0.0)",
					borderWidth: 2,
					pointBorderWidth: 2,
					pointRadius: 0,
					pointHitRadius: 12,
					type: "line",
					lineTension: 0,
					data: self.getDataArray(chartData.previousYear),
					stack: "previous"
				});

				dataByChannel.push({
					label: "Hourly Goal",
					backgroundColor: "rgba(229,163,75,0.0)",
					borderColor: "rgba(229,163,75,0.7)",
					pointBorderColor: "rgba(229,163,75,0.0)",
					pointBackgroundColor: "rgba(229,163,75,0.0)",
					borderWidth: 2,
					pointBorderWidth: 2,
					pointRadius: 0,
					pointHitRadius: 12,
					type: "line",
					lineTension: 0,
					borderDash: [5, 3],
					data: self.getDataArray(chartData.hourlyGoal),
					stack: "goal"
				});

				console.log(dataByChannel);
				
				myChart = new Chart(ctx, {
					type: 'bar',
					data: {
						labels: self.makeLabels(chartData),
						datasets: dataByChannel
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						legend: {
							position: 'bottom',
							labels: {
								boxWidth: 15,
								filter: (legendItem) => {
									return legendItem.text !== "Previous Year" && legendItem.text !== "Projection" && legendItem.text !== "Hourly Goal";
								}
							}
						},
						tooltips: {
							enabled: false
						},
						scales: {
							yAxes: [{
								//stacked: true,
								ticks: {
									beginAtZero: true,
									max: chartData.fullChartVerticalMax === chartData.focusedChartVerticalMax ? undefined : chartData.focusedChartVerticalMax
								}
							}],
							xAxes: [{
								//stacked: true,
								type: 'time',
								time: {
									displayFormats: {
										day: "H"
									}
								}
							}]
						}
					}
				});

				chartCreated = true;
			},

			resizeChart() {
				setTimeout(function() {
					myChart.resize();
				}, 2000);
			},

			refreshChart(chartData) {
				const self = this;

				let newCurrent = chartData.current[0][0];

				if (latestLabel !== newCurrent) {
					myChart.data.labels = self.makeLabels(chartData);
				}

				myChart.data.datasets.forEach(d => {

					switch (d.label) {
						case "Hourly Goal":
							d.data = self.getDataArray(chartData.hourlyGoal);
							break;
						case "Previous Year":
							d.data = self.getDataArray(chartData.previousYear);
							break;
						case "Projection":
							d.data = self.getCurrentHourPrediction(chartData);
							break;
						default:
							let channel = channelSpecs.filter(ch => ch.channelLabel === d.label);
							if (channel.length > 0) d.data = self.getDataArray(chartData.countByChannel[channel[0].channelKey]);
					}
				});
				myChart.options.scales.yAxes[0].ticks.max = chartData.fullChartVerticalMax === chartData.focusedChartVerticalMax ? undefined : chartData.focusedChartVerticalMax;
				myChart.update();
			}
		}
	}
</script>

<style lang="less">
	@import '../../less/_variables.less';

	@bg-chart: #DBDBDB;
	@bg-allorders: #93C459;
	@bg-flagorders: #B24434;
	@bg-shiporders: #E5A34B;
	@bg-pickuporders: #38495C;
	@bg-lighten: rgba(255,255,255,0.15);
	@bg-darken: rgba(0,0,0,0.15);
	@bg-summary: #333;

	.blain-dash {
		font-family: @font-main;
		padding: 0;
		height: 100%;
		color: #fff;
		display: flex;
		flex-direction: column;

		.header {
			flex: 0 0 auto;
			width: 100%;
			padding: 2% 2% 1%;
		}

		h1 {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			margin: 0;
			font-size: 2.5em;
			line-height: 1em;
			height: 100%;

			span.logo {
				background-image: url(images/blain.svg);
				background-size: contain;
				background-position: center center;
				background-repeat: no-repeat;
				width: 3.5em;
				height: 1.7em;
			}

			span.text {
				text-transform: uppercase;
				padding-left: 0.5em;
			}
		}

		.main-wrapper {
			flex: 0 1 auto;
			display: flex;
			flex-direction: column;
			width: 100%;
		}

		.top-wrapper {
			display: flex;
			justify-content: space-between;
			height: 55%;
			flex: 0 0 55%;
			padding: 0 2% 0.5%;
			width: 100%;
		}

		.data-squares {
			width: 37%;
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
		}

		.data-square-row {
			display: flex;
			justify-content: space-between;
			height: 47%;
		}

		.data-square {
			border-radius: @border-radius;
			width: 48%;
			height: 100;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			overflow: hidden;

			h2 {
				flex: 0 0 auto;
				background-color: @bg-lighten;
				margin: 0;
				padding: 0.2em;
				text-align: center;
				font-size: 1.2em;
				font-weight: 400;
				text-transform: uppercase;
			}

			.data {
				text-align: center;
				flex: 1 1 auto;
				display: flex;
				flex-direction: column;
				justify-content: center;

				strong {
					white-space: nowrap;
					font-weight: 700;
					font-size: 3em;
					line-height: 1em;
					padding: 0 0.3em 0.1em;
				}

				span {
					white-space: nowrap;
					font-size: 1em;
					line-height: 1em;
					padding: 0 0.3em;
					font-weight: 300;
				}
			}

			.footer {
				flex: 0 0 auto;
				display: flex;
				text-align: center;

				span {
					white-space: nowrap;
					font-size: 1em;
					padding: 0.3em 0.3em 0.1em;
					font-weight: 300;
				}

				strong {
					white-space: nowrap;
					font-weight: 700;
					font-size: 1.2em;
					padding: 0 0.3em 0.3em;
				}

				.col1,
				.col2,
				.col3 {
					height: 100%;
					overflow: hidden;
					display: flex;
					flex-direction: column;
					justify-content: center;
					background-color: @bg-darken;
				}

				.col1,
				.col2 {
					border-right: 2px solid @bg-lighten;
				}

				.col1,
				.col3 {
					width: 33.3%;
				}

				.col2 {
					width: 33.4%;
				}
			}

			&.all {
				background-color: @bg-allorders;
			}

			&.flag {
				background-color: @bg-flagorders;
			}

			&.ship {
				background-color: @bg-shiporders;
			}

			&.pickup {
				background-color: @bg-pickuporders;
			}
		}

		.chart-wrapper {
			width: 61%;
			height: 100%;
			background-color: @bg-chart;
			border-radius: @border-radius;
			display: flex;
			flex-direction: column;
			color: #000;
			overflow: hidden;

			.chart-stats {
				flex: 0 0 auto;
				display: flex;
				padding: 0.6em 1em;
				font-size: 0.9vw;

				strong {
					font-size: 1.5em;
				}
			}

			.chart-inner-wrapper {
				flex: 1 1 auto;
				padding: 0 1em;
			}

			.chart-footer {
				flex: 0 0 auto;
				padding: 0.8em 1em;
			}
		}

		.middle-wrapper {
			display: flex;
			padding: 0 1%;
			flex: 0 0 auto;
		}

		.data-item {
			overflow: hidden;
			width: 25%;

			&.short {
				width: 14%;
			}

			&.mid {
				width: 18%;
			}

			&.long {
				width: 27%;
			}

			padding: 0 1%;
			display: flex;
			align-items: center;
			justify-content: flex-start;

			span {
				&.label {
					text-transform: uppercase;
				}

				padding: 0.1em 0 0;
				font-size: 1em;
				font-weight: 300;
				white-space: nowrap;
			}

			.lastorder {
				padding: 0 0 0 1em;
				display: flex;
				flex-direction: column;
				justify-content: center;

				span {
					padding: 0;
					font-size: 0.9em;

					&.destination {
						font-size: 1.2em;
						font-weight: 700;
						text-transform: uppercase;
					}
				}
			}

			strong {
				font-size: 1.65em;
				font-weight: 700;
				padding: 0 0.1em 0 0.5em;
			}

			span.user-count {
				display: inline-block;
				width: 33%;
			}

			.user-count strong {
				font-size: 1.5em;
			}

			span.user-icon {
				font-size: 1.5em;
				padding: 0 0.1em 0 0.5em;
			}
		}

		.bottom-wrapper {
			display: flex;
			justify-content: space-between;
			flex: 1 1 auto;
			padding: 0.5% 2% 2%;
			width: 100%;
			font-weight: 300;

			h2 {
				text-transform: uppercase;
				padding: 0.5em 1em 0;
				margin: 0;
				font-size: 1.8em;
				text-align: center;
				flex: 0 0 auto;
			}

			h3 {
				margin: 0;
				padding: 0;
				text-align: center;
				font-weight: 300;
				font-size: 0.9em;
				flex: 0 0 auto;
			}

			.system-summary {
				border-radius: @border-radius;
				background-color: @bg-summary;
				width: 20%;
				overflow: hidden;
				display: flex;
				flex-direction: column;
			}

			.summary-wrapper {
				display: flex;
				flex-direction: column;
				justify-content: flex-start;
				padding-top: 0.3em;
				flex: 1 1 auto;
			}

			.summary-row {
				display: flex;
				font-size: 1.2em;
				justify-content: space-between;
				align-items: center;
				padding: 0.2em 1em;

				strong {
					font-size: 1.4em;
				}
			}

			.global-avail {
				border-radius: @border-radius;
				background-color: @bg-summary;
				width: 35%;
				overflow: hidden;
				display: flex;
				flex-direction: column;
			}

			.avail-wrapper {
				column-count: 2;
				column-width: auto;
				list-style: none;
				margin: 0;
				padding: 0.3em 0 0;
				flex: 0 1 auto;

				li {
					padding: 0.16em 1em;

					i {
						padding-right: 0.5em;
					}
				}
			}

			.api-wrapper {
				column-count: 3;
				column-width: auto;
				list-style: none;
				margin: 0;
				padding: 0.3em 0 0;
				flex: 0 1 auto;

				li {
					width: 100%;
					display: flex;
					justify-content: space-between;
					align-items: center;
					padding: 0.21em 1em;
				}
			}

			.avail-row,
			.api-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 0.21em 1.5em;
			}

			.avail-row {
				width: 50%;
			}

			.api-row {
				width: 33.33%;
			}

			.api-issues {
				border-radius: @border-radius;
				background-color: @bg-summary;
				width: 42%;
				overflow: hidden;
				display: flex;
				flex-direction: column;
			}
		}

		.ok {
			color: #0c0;
		}

		.notice {
			color: #8a0;
		}

		.warning {
			color: #fc0;
		}

		.critical {
			color: #f66;
			animation: 1.5s critical infinite;
		}

		.green {
			color: #090;
		}

		.red {
			color: #c00;
		}

		.gold {
			color: #c90;
		}
	}

	@keyframes critical {
		0% { opacity: 1; }
		49% { opacity: 1; }
		50% { opacity: 0; }
		99% { opacity: 0; }
		100% { opacity: 1; }
	}
</style>


// WEBPACK FOOTER //
// blain-dash.vue?5528f4e0